import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ScanLine, ShieldCheck, Sparkles, BadgeCheck, ArrowRight, ArrowUpRight } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { IMG } from "@/constants/images";

const A = {
  hero: "https://images.pexels.com/photos/33529500/pexels-photo-33529500.jpeg?auto=compress&cs=tinysrgb&w=1920",
  homes: "https://images.unsplash.com/photo-1646987916641-1f3c8992daa2?auto=format&fit=crop&w=1200&q=80",
  offices: "https://images.unsplash.com/photo-1699621106755-4fe40ce95d64?auto=format&fit=crop&w=1200&q=80",
  restaurants: "https://images.unsplash.com/photo-1574966739987-65e38db0f7ce?auto=format&fit=crop&w=1200&q=80",
  hotels: "https://images.unsplash.com/photo-1590381105924-c72589b9ef3f?auto=format&fit=crop&w=1200&q=80",
  statement: "https://images.pexels.com/photos/8135267/pexels-photo-8135267.jpeg?auto=compress&cs=tinysrgb&w=1920",
};

const ECOSYSTEM = [
  { icon: ScanLine, tag: "Scan", desc: "AI-powered surface analysis that identifies your material and its needs." },
  { icon: ShieldCheck, tag: "Protect", desc: "SPF — Surface Protection Film, protective coatings and specialty systems." },
  { icon: Sparkles, tag: "Preserve", desc: "Ongoing maintenance, surface spa and professional care that lasts." },
  { icon: BadgeCheck, tag: "Track", desc: "Digital warranty and a complete protection history for every surface." },
];

const PROTECT = [
  ["Tables", IMG.diningTable], ["Sofas", IMG.sofa], ["Kitchens", IMG.kitchen],
  ["Fabrics", IMG.velvetSofa], ["Glass", IMG.glass], ["Furniture", IMG.furniture],
];

const INDUSTRIES = [
  ["Homes", "Where life happens — protected for longer.", A.homes],
  ["Offices", "Premium workspaces that stay pristine.", A.offices],
  ["Restaurants", "High-traffic surfaces, effortlessly maintained.", A.restaurants],
  ["Hotels & Hospitality", "Luxury that endures guest after guest.", A.hotels],
];

const SURFACES = ["Dining Tables", "Furniture", "Kitchen Countertops", "Sofas", "Leather", "Suede", "Velvet", "Curtains", "Rugs", "Glass", "Marble", "Wood", "Stone"];

export default function About() {
  const navigate = useNavigate();
  return (
    <div className="bg-[#080808]" data-testid="about-page">
      <Seo title="About ERRAH — Beautiful Spaces, Protected for Longer" description="ERRAH protects the materials you live with every day — marble, wood, leather, velvet, glass and more — with SPF Surface Protection Film, coatings and professional surface care." />

      {/* 1. HERO */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden">
        <motion.img initial={{ scale: 1.12 }} animate={{ scale: 1 }} transition={{ duration: 1.8, ease: [0.16, 1, 0.3, 1] }}
          src={A.hero} alt="Luxury living interior" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/25" />
        <div className="relative max-w-[1500px] mx-auto px-5 lg:px-10 w-full pb-16 lg:pb-24">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.2 }} className="errah-label gold-text mb-5">ERRAH — Style Your Space</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.1, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="serif-head text-4xl sm:text-6xl lg:text-8xl max-w-4xl">
            Beautiful spaces<br />deserve <span className="gold-text italic">protection.</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.45 }}
            className="mt-7 text-base lg:text-lg text-gray-300 max-w-2xl font-light leading-relaxed">
            Your home tells a story. The dining table where people gather. The sofa where life happens. The kitchen where memories are made. ERRAH exists to keep the materials that make a space yours beautiful for longer.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.6 }} className="mt-9 flex flex-wrap gap-3">
            <button onClick={() => navigate("/ai-scan")} data-testid="about-scan-cta"
              className="inline-flex items-center gap-2 bg-[#C9A45C] text-black px-8 py-4 text-sm font-semibold tracking-wide hover:bg-[#E4C987] hover:-translate-y-0.5 transition-all">
              <ScanLine size={17} /> SCAN YOUR SURFACE
            </button>
            <Link to="/protection" data-testid="about-explore-cta"
              className="inline-flex items-center gap-2 border border-white/30 px-8 py-4 text-sm font-semibold tracking-wide hover:bg-white hover:text-black transition-colors">
              EXPLORE PROTECTION
            </Link>
          </motion.div>
        </div>
      </section>

      {/* 2. BRAND STORY — warm ivory */}
      <section className="bg-[#F4EEE2] text-[#141210]">
        <div className="max-w-[1500px] mx-auto px-5 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-[1fr_1.1fr] gap-12 lg:gap-20 items-center">
          <Reveal>
            <span className="inline-block h-px w-16 bg-[#C9A45C] mb-6" />
            <h2 className="font-serif text-3xl lg:text-5xl leading-[1.1] tracking-tight">Protection, designed around your space.</h2>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="text-lg leading-relaxed text-[#3a352e]">
              ERRAH brings together advanced <span className="font-semibold text-[#141210]">SPF — Surface Protection Film</span>, protective coatings and professional surface care to protect the materials you live with every day.
            </p>
            <div className="mt-8 flex flex-wrap gap-2.5" data-testid="about-surfaces">
              {SURFACES.map((s) => (
                <span key={s} className="text-sm px-4 py-2 rounded-full border border-[#141210]/15 bg-white/40 text-[#3a352e]">{s}</span>
              ))}
              <span className="text-sm px-4 py-2 text-[#8a7f6b] italic">& more compatible surfaces</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 3. ERRAH ECOSYSTEM */}
      <section className="max-w-[1500px] mx-auto px-5 lg:px-10 py-20">
        <Reveal>
          <p className="errah-label gold-text mb-4">The ERRAH ecosystem</p>
          <h2 className="serif-head text-3xl lg:text-5xl max-w-2xl">Everything your surfaces need — in one place.</h2>
        </Reveal>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-3.5" data-testid="about-ecosystem">
          {ECOSYSTEM.map((e, i) => (
            <Reveal key={e.tag} delay={i * 0.08}>
              <div className="group hair-border bg-white/[0.02] p-7 h-full transition-colors hover:bg-white/[0.05]">
                <e.icon className="text-[#C9A45C] transition-transform group-hover:-translate-y-0.5" size={28} />
                <p className="font-display text-2xl mt-6 tracking-tight">{e.tag}</p>
                <span className="block h-px w-8 bg-[#C9A45C]/50 my-3 transition-all group-hover:w-14" />
                <p className="text-sm text-gray-400 leading-relaxed">{e.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* 4. WHAT WE PROTECT */}
      <section className="max-w-[1500px] mx-auto px-5 lg:px-10 pb-20">
        <Reveal>
          <div className="flex items-end justify-between mb-8">
            <h2 className="serif-head text-3xl lg:text-5xl">What we protect.</h2>
            <Link to="/protection" className="hidden sm:inline-flex items-center gap-1 text-xs tracking-widest text-gray-400 hover:text-[#C9A45C] transition-colors">EVERY SURFACE <ArrowRight size={14} /></Link>
          </div>
        </Reveal>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3.5" data-testid="about-protect-grid">
          {PROTECT.map(([name, img], i) => (
            <Reveal key={name} delay={(i % 3) * 0.06}>
              <Link to="/protection" className="group block hover-zoom relative aspect-[4/3] overflow-hidden hair-border">
                <img src={img} alt={name} loading="lazy" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/25 to-transparent" />
                <div className="absolute bottom-0 inset-x-0 p-4 flex items-center justify-between">
                  <h3 className="font-serif text-xl">{name}</h3>
                  <ArrowUpRight size={18} className="text-[#C9A45C] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* 5. INDUSTRIES */}
      <section className="border-y border-white/10 bg-warmblack">
        <div className="max-w-[1500px] mx-auto px-5 lg:px-10 py-20">
          <Reveal>
            <p className="errah-label gold-text mb-4">Where ERRAH works</p>
            <h2 className="serif-head text-3xl lg:text-5xl max-w-2xl">Trusted across homes and hospitality.</h2>
          </Reveal>
          <div className="mt-10 grid sm:grid-cols-2 gap-3.5" data-testid="about-industries">
            {INDUSTRIES.map(([name, desc, img], i) => (
              <Reveal key={name} delay={(i % 2) * 0.08}>
                <div className="group relative aspect-[16/10] overflow-hidden hover-zoom hair-border">
                  <img src={img} alt={name} loading="lazy" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/10" />
                  <div className="absolute inset-0 p-7 lg:p-9 flex flex-col justify-end">
                    <h3 className="serif-head text-3xl lg:text-4xl">{name}</h3>
                    <p className="mt-2 text-sm text-gray-300 max-w-md">{desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 6. FINAL BRAND STATEMENT */}
      <section className="relative overflow-hidden">
        <img src={A.statement} alt="Luxury interior" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/80" />
        <div className="relative max-w-[1500px] mx-auto px-5 lg:px-10 py-28 lg:py-36 text-center">
          <Reveal>
            <h2 className="serif-head text-4xl sm:text-6xl lg:text-8xl leading-[1.02]">
              Live beautifully.<br /><span className="gold-text italic">We'll protect the rest.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-9 font-display text-xl tracking-[0.2em] text-white">ERRAH — STYLE YOUR SPACE</p>
            <p className="mt-2 errah-label gold-text">Protect · Preserve · Elevate</p>
            <div className="mt-10 flex flex-wrap justify-center gap-3">
              <button onClick={() => navigate("/ai-scan")} data-testid="about-final-scan-cta"
                className="inline-flex items-center gap-2 bg-[#C9A45C] text-black px-8 py-4 text-sm font-semibold tracking-wide hover:bg-[#E4C987] hover:-translate-y-0.5 transition-all">
                <ScanLine size={17} /> SCAN YOUR SURFACE
              </button>
              <button onClick={() => navigate("/quote")} data-testid="about-final-quote-cta"
                className="inline-flex items-center gap-2 border border-white/30 px-8 py-4 text-sm font-semibold tracking-wide hover:bg-white hover:text-black transition-colors">
                REQUEST A QUOTE
              </button>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
